import React, { useCallback, useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import Sidebar from "../Sidebar";
import Layout from "../Layout";
import Loader from "../../Loader";
import { GET_GROWTH_REPORTS } from "./graphql";

// Local calendar day as YYYY-MM-DD (toISOString would shift it to UTC).
const dayStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return dayStr(d);
};

const PRESETS = [
  { label: "Today", from: () => daysAgo(0) },
  { label: "7 days", from: () => daysAgo(6) },
  { label: "30 days", from: () => daysAgo(29) },
  { label: "90 days", from: () => daysAgo(89) },
];

const inr = (n) => `₹ ${Math.round(n || 0).toLocaleString("en-IN")}`;

const humanize = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());

const Card = ({ label, value, tone = "text-gray-800", hint }) => (
  <div className="bg-white shadow rounded-xl p-5 border border-gray-100">
    <p className="text-gray-500 text-sm">{label}</p>
    <h2 className={`text-2xl font-bold ${tone}`}>{value}</h2>
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

const Section = ({ title, subtitle, children }) => (
  <section className="mb-10">
    <div className="mb-3">
      <h2 className="text-lg font-bold text-gray-800">{title}</h2>
      {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
    </div>
    {children}
  </section>
);

const Table = ({ head, rows, empty }) => (
  <div className="overflow-x-auto border rounded-lg">
    <table className="min-w-full text-left text-sm text-gray-600">
      <thead className="bg-gray-50 text-gray-700 font-bold border-b">
        <tr>
          {head.map((h) => (
            <th key={h} className="py-3 px-4">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y">
        {rows.length === 0 ? (
          <tr>
            <td colSpan={head.length} className="text-center py-6 text-gray-400">
              {empty}
            </td>
          </tr>
        ) : (
          rows.map((cells, i) => (
            <tr key={i} className="hover:bg-gray-50 transition">
              {cells.map((c, j) => (
                <td key={j} className="py-3 px-4">
                  {c}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

const GrowthReports = () => {
  const [startDate, setStartDate] = useState(daysAgo(29));
  const [endDate, setEndDate] = useState(daysAgo(0));

  const [fetchReports, { data, loading, error }] = useLazyQuery(
    GET_GROWTH_REPORTS,
    { fetchPolicy: "network-only" },
  );

  const load = useCallback(
    (from = startDate, to = endDate) => {
      if (!from || !to || from > to) return;
      fetchReports({ variables: { data: { startDate: from, endDate: to } } });
    },
    [fetchReports, startDate, endDate],
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (preset) => {
    const from = preset.from();
    const to = daysAgo(0);
    setStartDate(from);
    setEndDate(to);
    load(from, to);
  };

  const report = data?.getGrowthReports;
  const ok = report?.status === "SUCCESS";
  const ref = report?.referrals;
  const cpn = report?.coupons;
  const wallet = report?.wallet?.rows || [];
  const topCoupon = cpn?.byCoupon?.[0];
  const maxDaily = Math.max(1, ...(ref?.daily || []).map((d) => d.count));

  const walletIn = wallet
    .filter((w) => w.type === "CREDIT" || w.type === "REFUND")
    .reduce((a, w) => a + w.amount, 0);
  const walletOut = wallet
    .filter((w) => w.type === "DEBIT" || w.type === "EXPIRED")
    .reduce((a, w) => a + w.amount, 0);

  return (
    <div className="min-h-screen flex">
      <Sidebar />

      <Layout>
        <div className="p-6 bg-white w-full rounded-lg shadow-md m-4 overflow-y-auto">
          {/* Header + shared period filter */}
          <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4 mb-6 border-b pb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Growth Reports
              </h1>
              <p className="text-sm text-gray-500">
                Referrals, coupon discounts and wallet movement for the
                selected period.
              </p>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div className="flex gap-2">
                {PRESETS.map((p) => (
                  <button
                    key={p.label}
                    onClick={() => applyPreset(p)}
                    className="px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <label className="text-sm text-gray-600">
                From
                <input
                  type="date"
                  value={startDate}
                  max={endDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="block border rounded-lg px-3 py-2"
                />
              </label>
              <label className="text-sm text-gray-600">
                To
                <input
                  type="date"
                  value={endDate}
                  min={startDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="block border rounded-lg px-3 py-2"
                />
              </label>
              <button
                onClick={() => load()}
                className="px-4 py-2 rounded-lg bg-[#191919] text-[#FBB033] font-semibold"
              >
                Apply
              </button>
            </div>
          </div>

          {loading && <Loader />}

          {!loading && (error || (report && !ok)) && (
            <div className="rounded-lg border border-dashed border-red-300 bg-red-50 p-6 text-center text-red-600">
              {report?.message || "Error fetching reports."}
            </div>
          )}

          {!loading && ok && (
            <>
              {/* 1. REFERRALS */}
              <Section
                title="Referrals"
                subtitle="Referrals created in the period (a referral is created when a new user applies a code)."
              >
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                  <Card label="Total referrals" value={ref.total} />
                  <Card
                    label="Converted"
                    value={ref.converted}
                    tone="text-green-600"
                    hint="Referee completed a booking"
                  />
                  <Card
                    label="Conversion rate"
                    value={`${ref.conversionRate}%`}
                    tone="text-blue-600"
                  />
                  <Card
                    label="Rewards paid"
                    value={inr(ref.rewardsPaid)}
                    tone="text-amber-600"
                  />
                </div>

                {ref.daily.length > 0 && (
                  <div className="border rounded-lg p-4 mb-5">
                    <p className="text-sm font-semibold text-gray-700 mb-3">
                      Referrals per day
                    </p>
                    <div className="flex items-end gap-1 h-28 overflow-x-auto">
                      {ref.daily.map((d) => (
                        <div
                          key={d.date}
                          title={`${d.date}: ${d.count}`}
                          className="flex-1 min-w-[6px] bg-[#FBB033] rounded-t"
                          style={{ height: `${(d.count / maxDaily) * 100}%` }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      By status
                    </p>
                    <Table
                      head={["Status", "Count"]}
                      rows={ref.byStatus.map((s) => [
                        humanize(s.status),
                        s.count,
                      ])}
                      empty="No referrals in this period."
                    />
                  </div>
                  <div className="lg:col-span-2">
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      Top referrers
                    </p>
                    <Table
                      head={["Referrer", "Phone", "Referrals", "Converted"]}
                      rows={ref.topReferrers.map((r) => [
                        <span className="font-semibold text-gray-800">
                          {r.name}
                        </span>,
                        r.phone || "-",
                        r.referrals,
                        r.converted,
                      ])}
                      empty="No referrals in this period."
                    />
                  </div>
                </div>
              </Section>

              {/* 2. COUPONS */}
              <Section
                title="Coupons"
                subtitle="Discount given through coupons on non-cancelled bookings created in the period."
              >
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                  <Card
                    label="Total discount given"
                    value={inr(cpn.totalDiscount)}
                    tone="text-red-600"
                  />
                  <Card
                    label="Bookings with a coupon"
                    value={cpn.ordersWithCoupon}
                  />
                  <Card
                    label="Avg discount / booking"
                    value={inr(cpn.avgDiscount)}
                  />
                  <Card
                    label="Most used coupon"
                    value={topCoupon ? topCoupon.code : "-"}
                    tone="text-green-600"
                    hint={topCoupon ? `${topCoupon.uses} uses` : undefined}
                  />
                </div>
                <Table
                  head={["Coupon", "Uses", "Unique users", "Total discount"]}
                  rows={cpn.byCoupon.map((c) => [
                    <span className="font-semibold text-gray-800">
                      {c.code}
                    </span>,
                    c.uses,
                    c.users,
                    <span className="text-red-600 font-medium">
                      {inr(c.discount)}
                    </span>,
                  ])}
                  empty="No coupons used in this period."
                />
              </Section>

              {/* 3. WALLET */}
              <Section
                title="Wallet"
                subtitle="Completed wallet transactions in the period, by category."
              >
                <div className="grid grid-cols-2 gap-4 mb-5">
                  <Card
                    label="Added to wallets"
                    value={inr(walletIn)}
                    tone="text-green-600"
                    hint="Credits + refunds"
                  />
                  <Card
                    label="Used / expired"
                    value={inr(walletOut)}
                    tone="text-red-600"
                    hint="Debits + expired credits"
                  />
                </div>
                <Table
                  head={["Category", "Type", "Transactions", "Amount"]}
                  rows={wallet.map((w) => [
                    humanize(w.category),
                    humanize(w.type),
                    w.count,
                    inr(w.amount),
                  ])}
                  empty="No wallet activity in this period."
                />
              </Section>
            </>
          )}
        </div>
      </Layout>
    </div>
  );
};

export default GrowthReports;
